import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PlanConfigModal = ({ plan, onEnroll, onClose, enrolling = false }) => {
  const [config, setConfig] = useState({
    bibleVersion: 'kjv',
    emailNotifications: false,
    startDate: new Date()?.toISOString()?.split('T')?.[0]
  });
  const [bibleVersions, setBibleVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(true);

  // Fetch available Bible versions
  useEffect(() => {
    fetchBibleVersions();
  }, []);

  const fetchBibleVersions = async () => {
    try {
      const { data, error } = await supabase
        ?.from('bible_versions')
        ?.select('id, short_name, version_name')
        ?.eq('is_enabled', true)
        ?.order('short_name');

      if (error) throw error;

      setBibleVersions(data || []);
    } catch (error) {
      console.error('Error fetching Bible versions:', error);
      // Set default versions if fetch fails
      setBibleVersions([
        { id: 1, short_name: 'kjv', version_name: 'King James Version' },
        { id: 2, short_name: 'asv', version_name: 'American Standard Version' }
      ]);
    } finally {
      setLoadingVersions(false);
    }
  };

  const bibleVersionOptions = bibleVersions?.map(version => ({
    value: version?.short_name?.toLowerCase(),
    label: `${version?.version_name} (${version?.short_name?.toUpperCase()})`
  }));

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onEnroll?.(plan, config);
  };

  const calculateEndDate = (startDate, durationDays) => {
    const start = new Date(startDate);
    const end = new Date(start);
    end?.setDate(start?.getDate() + durationDays);
    return end?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const durationDays = plan?.duration_days || 365;
  const estimatedTime = plan?.estimated_daily_reading_time || 15;
  const totalReadings = plan?.total_readings || durationDays;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card border border-border rounded-xl shadow-warm max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name="Settings" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">
                Configure Reading Plan
              </h2>
              <p className="text-sm text-muted-foreground">{plan?.name}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            disabled={enrolling}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Plan Overview */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="font-medium text-foreground mb-2">Plan Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Duration:</span>
                <span className="ml-2 font-medium text-foreground">{durationDays} days</span>
              </div>
              <div>
                <span className="text-muted-foreground">Daily Time:</span>
                <span className="ml-2 font-medium text-foreground">~{estimatedTime} min</span>
              </div>
              <div>
                <span className="text-muted-foreground">Category:</span>
                <span className="ml-2 font-medium text-foreground">{plan?.category}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Total Readings:</span>
                <span className="ml-2 font-medium text-foreground">{totalReadings}</span>
              </div>
            </div>
          </div>

          {/* Configuration Options */}
          <div className="space-y-4">
            {/* Bible Version */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Bible Version
              </label>
              {loadingVersions ? (
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Loader" size={14} className="animate-spin" />
                  <span>Loading versions...</span>
                </div>
              ) : (
                <Select
                  value={config?.bibleVersion}
                  onValueChange={(value) => handleConfigChange('bibleVersion', value)}
                  options={bibleVersionOptions}
                />
              )}
              <p className="text-xs text-muted-foreground">
                You can change the Bible version anytime during the plan
              </p>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Start Date
              </label>
              <Input
                type="date"
                value={config?.startDate}
                onChange={(e) => handleConfigChange('startDate', e?.target?.value)}
                min={new Date()?.toISOString()?.split('T')?.[0]}
              />
              <p className="text-xs text-muted-foreground">
                Expected completion: {calculateEndDate(config?.startDate, durationDays)}
              </p>
            </div>

            {/* Email Reminders */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="emailNotifications"
                  checked={config?.emailNotifications}
                  onChange={(e) => handleConfigChange('emailNotifications', e?.target?.checked)}
                  className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                />
                <label htmlFor="emailNotifications" className="text-sm font-medium text-foreground">
                  Send daily email reminders
                </label>
              </div>
              <p className="text-xs text-muted-foreground ml-7">
                Receive daily reading assignments via email with direct links to mark completion
              </p>
            </div>
          </div>

          {/* Tags Preview */}
          {plan?.tags?.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-medium text-foreground">Plan Tags</h3>
              <div className="flex flex-wrap gap-2">
                {plan?.tags?.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={enrolling}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={enrolling || loadingVersions}
            >
              {enrolling ? (
                <>
                  <Icon name="Loader" size={16} className="animate-spin" />
                  Enrolling...
                </>
              ) : (
                <>
                  <Icon name="Play" size={16} />
                  Start Reading Plan
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanConfigModal;